import AppLayout from "@/components/layouts/Layout/AppLayout";
import { AuthLayout } from "@/components/layouts/Layout/AuthLayout";
import { ForgotPassword, Login, ResetPassword } from "@/pages/auth";

import * as Sentry from "@sentry/react";
import { createBrowserRouter, RouterProvider } from "react-router";

import FeeForm from "@/pages/Expenses/Fees/form";
import FuellingsForm from "@/pages/Expenses/Fuellings/form";
import InsuranceForm from "@/pages/Expenses/Insurances/form";
import LicensingForm from "@/pages/Expenses/Licensings/form";
import MaintenanceForm from "@/pages/Expenses/Maintenance/form";
import RepairForm from "@/pages/Expenses/Repairs/form";
import RevenueForm from "@/pages/Expenses/Revenues/form";
import TicketForm from "@/pages/Expenses/Tickets/form";
import AccessLevelForm from "@/pages/Registrations/AccessLevels/form";
import ClientProvidersForm from "@/pages/Registrations/ClientProviders/form";
import DriverForm from "@/pages/Registrations/Drivers/form";
import ReferencePointForm from "@/pages/Registrations/ReferencePoints/form";
import SafeZoneForm from "@/pages/Registrations/SafeZones/form";
import UserForm from "@/pages/Registrations/Users/form";
import VehicleGroupsForm from "@/pages/Registrations/VehicleGroups/form";
import VisitObjectiveForm from "@/pages/Registrations/VisitObjectives/form";
import VisitForm from "@/pages/Registrations/Visits/form";
import MapReport from "@/pages/Reports/MapReport/index";
import TravelReport from "@/pages/Reports/TravelReport/index";

import GeofencesForm from "@/pages/Security/Geofences/form";
import DriverLeavesForm from "@/pages/WorkJourney/DriverLeaves/form";
import DriverVacationsForm from "@/pages/WorkJourney/DriverVacations/form";

import ExpensesReport from "@/pages/Reports/ExpensesReport";
import HoursOfServiceReportForm from "@/pages/Reports/HoursOfServiceReport/form";
import PositionsReport from "@/pages/Reports/PositionsReport";
import VehicleJourneyReport from "@/pages/Reports/VehicleJourneyReport";
import VisitsReport from "@/pages/Reports/VisitsReport";
import MyServiceOrdersForm from "@/pages/UserMenu/MyServiceOrders/form";

import RouterErrorBoundary from "@/components/RouterErrorBoundary";
import Downloads from "@/pages/Downloads";
import EndCustomerForm from "@/pages/Routing/EndCustomer/form";
import RouteForm from "@/pages/Routing/VehicleRouting/form";
import React, { Suspense } from "react";
import { ProtectedRoute, ProtectedRouteLayout } from "./ProtectedRoute";
import { RequireModule } from "./RequireModule";

const LazyPortableTrackers = React.lazy(
  () => import("../pages/PortableTrackers"),
);

const LazyUsers = React.lazy(() => import("../pages/Registrations/Users"));
const LazyAccessLevels = React.lazy(
  () => import("../pages/Registrations/AccessLevels"),
);
const LazyDrivers = React.lazy(() => import("../pages/Registrations/Drivers"));
const LazyTickets = React.lazy(() => import("../pages/Expenses/Tickets"));
const LazyTicketView = React.lazy(
  () => import("../pages/Expenses/Tickets/view"),
);
const LazyLicensings = React.lazy(() => import("../pages/Expenses/Licensings"));
const LayzInsurances = React.lazy(() => import("../pages/Expenses/Insurances"));
const LazyMaintenances = React.lazy(
  () => import("../pages/Expenses/Maintenance"),
);
const LazyFees = React.lazy(() => import("../pages/Expenses/Fees"));
const LazyRepairs = React.lazy(() => import("../pages/Expenses/Repairs"));
const LazyRevenues = React.lazy(() => import("../pages/Expenses/Revenues"));
const LazyFuellings = React.lazy(() => import("../pages/Expenses/Fuellings"));
const LazyClientProviders = React.lazy(
  () => import("../pages/Registrations/ClientProviders"),
);
const LazyNotificationPreferences = React.lazy(
  () => import("../pages/Registrations/UserEventNotificationPreferences"),
);

const LazyReferencePoints = React.lazy(
  () => import("../pages/Registrations/ReferencePoints"),
);

const LazySafeZones = React.lazy(
  () => import("../pages/Registrations/SafeZones"),
);

const LazyVisits = React.lazy(() => import("../pages/Registrations/Visits"));

const LazyVisitObjectives = React.lazy(
  () => import("../pages/Registrations/VisitObjectives"),
);

const LazyGeofences = React.lazy(() => import("../pages/Security/Geofences"));

const LazyDriverDailyCredits = React.lazy(
  () => import("../pages/WorkJourney/DriverDailyCredits"),
);

const LazyAlarms = React.lazy(() => import("../pages/Security/Alarms"));

const LazyDriverLeaves = React.lazy(
  () => import("../pages/WorkJourney/DriverLeaves"),
);

const LazyDriverVacations = React.lazy(
  () => import("../pages/WorkJourney/DriverVacations"),
);

const LazyVehicleGroups = React.lazy(
  () => import("../pages/Registrations/VehicleGroups"),
);

const LazyDriverBehaviorReport = React.lazy(
  () => import("../pages/Reports/DriverBehaviorReport"),
);

const LazyFleetDrivingRanking = React.lazy(
  () => import("../pages/Reports/FleetDrivingRanking"),
);

const LazyVehicleJourneySummary = React.lazy(
  () => import("../pages/Reports/VehicleJourneySummary"),
);

const LazyYearlyExpensesReport = React.lazy(
  () => import("../pages/Reports/YearlyExpensesReport"),
);

const LazyMaintenanceAnalyticsReport = React.lazy(
  () => import("../pages/Reports/MaintenanceAnalyticsReport"),
);

const LazyTrunkReport = React.lazy(
  () => import("../pages/Reports/TrunkReport"),
);

const LazyFleetSummaryReport = React.lazy(
  () => import("../pages/Reports/FleetSummaryReport"),
);

const LazyFuellingReport = React.lazy(
  () => import("../pages/Reports/FuellingReport"),
);

const LazyTemperatureReport = React.lazy(
  () => import("../pages/Reports/TemperatureReport"),
);

const LazyEventReport = React.lazy(
  () => import("../pages/Reports/EventReport"),
);

const LazyMyServiceOrders = React.lazy(
  () => import("../pages/UserMenu/MyServiceOrders"),
);

const LazyDriverJourneyReport = React.lazy(
  () => import("../pages/Reports/DriverJourneyReport"),
);

const LazyDailyCreditsReport = React.lazy(
  () => import("../pages/Reports/DailyCreditsReport"),
);

const LazyMyInvoices = React.lazy(() => import("../pages/UserMenu/MyInvoices"));

const LazyMyData = React.lazy(() => import("../pages/UserMenu/MyData"));

const LazyHoursOfServiceReport = React.lazy(
  () => import("../pages/Reports/HoursOfServiceReport"),
);

const LazyTachographReport = React.lazy(
  () => import("../pages/Reports/TachographReport"),
);

const LazyCameraDashboard = React.lazy(
  () => import("../pages/VideoSurveillance/Dashboard"),
);
const LazyTrunkDashboard = React.lazy(
  () => import("../pages/Dashboard/TrunkDashboard"),
);
const LazyStreaming = React.lazy(
  () => import("../pages/VideoSurveillance/Streaming"),
);
const LazyVideos = React.lazy(
  () => import("../pages/VideoSurveillance/Videos"),
);
const LazyPlayback = React.lazy(
  () => import("../pages/VideoSurveillance/Playback"),
);

const LazyCamVideos = React.lazy(
  () => import("../pages/VideoSurveillance/CamVideos"),
);

const LazyVehicleRouting = React.lazy(
  () => import("../pages/Routing/VehicleRouting"),
);

const LazyRouteView = React.lazy(
  () => import("../pages/Routing/VehicleRouting/ViewRoute"),
);

const LazyEndCustomers = React.lazy(
  () => import("../pages/Routing/EndCustomer"),
);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: "/auth",
    element: <AuthLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
    ],
  },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        element: <ProtectedRouteLayout />,
        children: [
          {
            path: "",
            lazy: () => import("../pages/NoModules/NoModulesGuard"),
          },
          {
            path: "dashboard",
            lazy: () => import("../pages/Dashboard"),
          },
          {
            path: "assistente",
            lazy: () => import("../pages/Assistant"),
          },
          {
            path: "video-surveillance-reports",
            lazy: () => import("../pages/VideoSurveillance/Reports"),
          },
          {
            path: "my-fleet",
            children: [
              {
                path: "",
                lazy: () => import("../pages/MyFleet"),
              },
              {
                path: "overview",
                lazy: () => import("../pages/MyFleet/VehicleOverview"),
              },
              {
                path: "overview/:vehicleId",
                lazy: () => import("../pages/MyFleet/VehicleOverview"),
              },
              {
                path: ":id/edit",
                lazy: () => import("../pages/MyFleet/VehicleConfig"),
              },
            ],
          },
          {
            path: "api-integration",
            lazy: () => import("../pages/UserMenu/ApiIntegration"),
          },
          {
            path: "work-schedule",
            lazy: () => import("../pages/WorkJourney/WorkSchedule"),
          },
          {
            path: "work-journey-balance-report",
            lazy: () => import("../pages/WorkJourney/WorkJourneyBalance"),
          },
          {
            path: "heavy-drive-behavior",
            lazy: () => import("../pages/Reports/HeavyDriveBehaviorReport"),
          },
          {
            path: "engine-telemetry-report",
            lazy: () => import("../pages/Reports/EngineTelemetryReport"),
          },
          {
            path: "fuel-coaching",
            lazy: () => import("../pages/Reports/FuelCoachingReport"),
          },
          {
            path: "camera-risk-ranking",
            lazy: () => import("../pages/Reports/CameraRiskRanking"),
          },
          {
            path: "tickets/:id/indication",
            lazy: () => import("../pages/Expenses/Tickets/Indication"),
          },
        ],
      },
      {
        path: "portable-trackers",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Carregando...</div>}>
              <LazyPortableTrackers />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "users",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyUsers />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <UserForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <UserForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "access-levels",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyAccessLevels />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <AccessLevelForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <AccessLevelForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "drivers",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDrivers />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <DriverForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <DriverForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "licensings",
        children: [
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LicensingForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyLicensings />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LicensingForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "insurances",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LayzInsurances />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <InsuranceForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <InsuranceForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "tickets",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyTickets />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <TicketForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <TicketForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyTicketView />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/positions-report",
        element: (
          <ProtectedRoute>
            <PositionsReport />
          </ProtectedRoute>
        ),
      },
      {
        path: "/visits-report",
        element: (
          <ProtectedRoute>
            <VisitsReport />
          </ProtectedRoute>
        ),
      },
      {
        path: "/map-report",
        element: (
          <ProtectedRoute>
            <MapReport />
          </ProtectedRoute>
        ),
      },
      {
        path: "/tachograph-report",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Carregando...</div>}>
              <LazyTachographReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/vehicle-journey-report",
        element: (
          <ProtectedRoute>
            <VehicleJourneyReport />
          </ProtectedRoute>
        ),
      },
      {
        path: "/expenses-report",
        element: (
          <ProtectedRoute>
            <ExpensesReport />
          </ProtectedRoute>
        ),
      },
      {
        path: "/travel-report",
        element: (
          <ProtectedRoute>
            <TravelReport />
          </ProtectedRoute>
        ),
      },
      {
        path: "fuellings",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyFuellings />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <FuellingsForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <FuellingsForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "maintenances",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyMaintenances />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <MaintenanceForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <MaintenanceForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/fees",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyFees />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <FeeForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "repairs",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyRepairs />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <RepairForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <RepairForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "vehicle-revenues",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyRevenues />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <RevenueForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <RevenueForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "notification-preferences",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyNotificationPreferences />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "client-providers",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyClientProviders />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <ClientProvidersForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <ClientProvidersForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "reference-points",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyReferencePoints />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <ReferencePointForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <ReferencePointForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "safe-zones",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazySafeZones />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <SafeZoneForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <SafeZoneForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "visits",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyVisits />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <VisitForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <VisitForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "visit-objectives",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyVisitObjectives />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <VisitObjectiveForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <VisitObjectiveForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "geofences",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyGeofences />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <GeofencesForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <GeofencesForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "alarms",
        element: (
          <ProtectedRoute>
            <Suspense fallback={<div>Carregando...</div>}>
              <LazyAlarms />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "driver-leaves",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDriverLeaves />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <DriverLeavesForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <DriverLeavesForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "driver-vacations",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDriverVacations />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <DriverVacationsForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <DriverVacationsForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "groups",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyVehicleGroups />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <VehicleGroupsForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <VehicleGroupsForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "driver-daily-credits",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDriverDailyCredits />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "driver-behavior",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDriverBehaviorReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "fleet-driving-ranking",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyFleetDrivingRanking />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "vehicle-daily-journey",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyVehicleJourneySummary />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "trunk-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyTrunkReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "fuelling-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyFuellingReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "temperature-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyTemperatureReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "my-invoices",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyMyInvoices />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "yearly-expenses-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyYearlyExpensesReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "maintenance-analytics-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyMaintenanceAnalyticsReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "driver-journey-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDriverJourneyReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "events-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyEventReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "daily-credits-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyDailyCreditsReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "my-service-orders",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyMyServiceOrders />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <MyServiceOrdersForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "my-data",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyMyData />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "hours-of-service-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyHoursOfServiceReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <HoursOfServiceReportForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "camera-dashboard",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <RequireModule module="CAMERA_DASHBOARD">
                  <Suspense fallback={<div>Carregando...</div>}>
                    <LazyCameraDashboard />
                  </Suspense>
                </RequireModule>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "dashboard/trunk",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <RequireModule module="DASHBOARD_BAU">
                  <Suspense fallback={<div>Carregando...</div>}>
                    <LazyTrunkDashboard />
                  </Suspense>
                </RequireModule>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "streaming",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyStreaming />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "videos",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyVideos />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "playback",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyPlayback />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "camera-downloads",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyCamVideos />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "vehicle-routing",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyVehicleRouting />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <RouteForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/view",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyRouteView />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "end-customer",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyEndCustomers />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "create",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <EndCustomerForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/edit",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <EndCustomerForm />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "fleet-summary-report",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}>
                  <LazyFleetSummaryReport />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "downloads",
        children: [
          {
            path: "",
            element: (
              <ProtectedRoute>
                <Suspense fallback={<div>Carregando...</div>}></Suspense>
                <Downloads />
              </ProtectedRoute>
            ),
          },
          {},
        ],
      },
    ],
  },
  {
    path: "*",
    element: <div>Not found</div>,
  },
]);

const Routes = () => {
  return <RouterProvider router={router} />;
};

export default Routes;
